import { ClockIcon } from '@heroicons/react/outline';
import LightBulb from 'assets/svg/icons/LightBulb';
import BankIcon from 'components/atoms/SVG/sidebarIcons/BankIcon.component';
import ExpensesIcon from 'components/atoms/SVG/sidebarIcons/ExpensesIcon.component';
import RevenuesIcon from 'components/atoms/SVG/sidebarIcons/RevenuesIcon.component';
import { type FC } from 'react';
import type { ConverationIconType } from 'types/ai.types';
import { QuestionMarkCircleIconOutline } from 'utils/icons';

export const sharedMarkdownComponents = {
  h1: ({ children }: any) => (
    <h1 className="text-base font-semibold first:mt-0 mt-2 mb-1">{children}</h1>
  ),
  h2: ({ children }: any) => (
    <h2 className="font-semibold first:mt-0 mt-2 mb-1">{children}</h2>
  ),
  h3: ({ children }: any) => (
    <h3 className="font-semibold first:mt-0 mt-2 mb-1">{children}</h3>
  ),
  h4: ({ children }: any) => (
    <h4 className="font-semibold first:mt-0 mt-2 mb-1">{children}</h4>
  ),
  h5: ({ children }: any) => (
    <h5 className="font-semibold first:mt-0 mt-2 mb-1">{children}</h5>
  ),
  h6: ({ children }: any) => (
    <h6 className="font-semibold first:mt-0 mt-2 mb-1">{children}</h6>
  ),
  p: ({ children }: any) => <p>{children}</p>,
  ol: ({ ordered, ...props }: any) => (
    <ol className="!pl-6 !list-decimal" {...props} />
  ),
  ul: ({ ordered, ...props }: any) => (
    <ul className="!pl-6 !list-disc" {...props} />
  ),
  a: ({ children, href }: any) => (
    <a
      target="blank"
      rel="noopener noreferrer"
      className="text-taxes"
      href={href}
    >
      {children}
    </a>
  ),
  hr: ({ children }: any) => <hr className="my-6" />,
  strong: ({ children }: any) => (
    <span className="font-semibold">{children}</span>
  ),
  // Table components
  table: ({ children }: any) => (
    <div className="overflow-x-auto mt-4 mb-6">
      <table className="min-w-full border-collapse border border-gray-300">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }: any) => (
    <thead className="bg-gray-50">{children}</thead>
  ),
  tbody: ({ children }: any) => <tbody>{children}</tbody>,
  tr: ({ children }: any) => (
    <tr className="border-b border-gray-200">{children}</tr>
  ),
  th: ({ children }: any) => (
    <th className="border border-gray-300 px-4 py-2 text-left font-semibold bg-gray-100">
      {children}
    </th>
  ),
  td: ({ children }: any) => (
    <td className="border border-gray-300 px-4 py-2">{children}</td>
  ),
  // Code block component to handle mermaid
  code: ({ node, inline, className, children, ...props }: any) => {
    return inline ? (
      <code
        className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono"
        {...props}
      >
        {children}
      </code>
    ) : (
      <pre className="bg-gray-100 p-3 rounded-md overflow-x-auto my-2">
        <code className={className} {...props}>
          {children}
        </code>
      </pre>
    );
  },
};

export const scrollToBottom = () => {
  const chatBox = document.getElementById('chat-box') as HTMLElement;
  if (!chatBox) return;

  setTimeout(() => {
    chatBox.scrollTo({
      behavior: 'smooth',
      left: 0,
      top: chatBox.scrollHeight,
    });
  }, 200);
};

export const cardIconComponents: Record<
  ConverationIconType,
  FC<{ className?: string }>
> = {
  default: ClockIcon,
  gratifications: LightBulb,
  transaction: BankIcon,
  expense: ExpensesIcon,
  revenue: RevenuesIcon,
  helper: QuestionMarkCircleIconOutline,
  wrapped_2024: QuestionMarkCircleIconOutline, // TODO: double check if this needs to remain as a "source"
  repository_document: QuestionMarkCircleIconOutline,
};

export const cardIconColors: Record<
  ConverationIconType,
  { color: string; bg: string }
> = {
  default: { color: 'text-primary', bg: 'bg-primary-100' },
  gratifications: { color: 'text-taxes', bg: 'bg-taxes-100' },
  transaction: { color: 'text-bank', bg: 'bg-bank-100' },
  expense: { color: 'text-expenses', bg: 'bg-expenses-100' },
  revenue: { color: 'text-revenues', bg: 'bg-revenues-100' },
  helper: { color: 'text-primary', bg: 'bg-primary-100' },
  wrapped_2024: { color: 'text-primary-600', bg: 'bg-primary-100' }, // TODO: double check if this needs to remain as a "source"
  repository_document: { color: 'text-primary', bg: 'bg-primary-100' },
};
